
/**
 *      Data-driven UI that displays linked records on a single record view
 *
 *      @author Irina Patrikeeva <irina.i.patrikeeva@nasa.gov>
 *      Created on 02/13/2018
 *      Modified on 03/04/2019
 */

/**
 * Retrieve and show linked records
 * @param data {Array} - array of linked records
 * @param layoutModel {object} - region view model
 * @param total {number} - total number of items in the table
 */
function createLinkedTable( data, layoutModel, total ) {

    // check if data models for linked data from the response exist locally
    var types,
        models,
        result,
        k;

    if ( data ) {
        result = getLinkedModelsAndTypesFromData( data );
        if ( result ) {
            types = result.types;
            models = result.models;
        }
    }

    if ( types && models ) {
        if ( types.length !== 0 && types.length !== models.length ) {
            // Get UI models from DMR for each type
            for ( k = 0; k < types.length; k++ ) {
                getCDMModel( types[k], getLinkedModels( types[ k ], types, data, layoutModel, total ), dataNotFound( "Get linked models for type " + types[ k ] ) );
            }
        } else {
            showLinkedRecords( types, data, layoutModel, total );
        }
    }

}

/**
 * Get CDM models from global store and record types
 * @param data {Array} -  array of linked records
 * @return {{models: Array, types: Array}}
 */
function getLinkedModelsAndTypesFromData( data ) {
    var models = [],
        types = [],
        record,
        type,
        isNewType,
        model,
        i,
        j;

    if ( data ) {
        for ( i = 0; i < data.length; i++ ) {
            record = data[ i ];
            // Check record._type attribute, which is found on the first level of the record object
            if ( record && record._type ) {
                type = record._type;
                // check if type already exist in the array
                isNewType = true;
                for ( j = 0; j < types.length; j++ ) {
                    if ( types[ j ] === type ) {
                        isNewType = false;
                    }
                }

                if ( isNewType ) {
                    types.push( type );
                }
                // Get CDM model from global store
                model = getCommonDataModel( type );
                if ( model ) {
                    // display data
                    models.push( model) ;
                }
            }
        }
    }

    return { models: models, types: types };
}
/**
 * Return a callback function after UI model has been fetched from DMR to show data type aggregations
 * @param name {string} - model name
 * @param types {Array} - array of model names (data types)
 * @param records {Array} - array of linked records
 * @param layoutModel {object} - region view model
 * @param total {number} - total number of items in the table
 * @returns {Function}
 */
function getLinkedModels( name, types, records, layoutModel, total ) {

    /**
     * Callback that is called when the call to fetch the model from Data Model Registry succeeds
     * @param data {object} - data model JSON object
     * @param textStatus {string} - status of the request
     * @param jqXHR {object} - jQuery XMMLHTTPRequest object
     */
    return function( data, textStatus, jqXHR ) {
        var model = data;

        if ( model && types ) {

            // save the CDM model into the GlobalState
            saveCommonDataModel( name, model );

            if ( GlobalPageState && GlobalPageState.models && Object.keys( GlobalPageState.models ).length >= types.length ) {
                showLinkedRecords( types, records, layoutModel, total );
            }
        }
    };
}

/**
 * Show linked records in the layout
 * @param types {Array} - array of type names
 * @param data {Array} - array of linked records
 * @param layoutModel {object} - JSON region view model
 * @param total {number} - total number of items in the table
 */
function showLinkedRecords( types, data, layoutModel, total ) {

    // All models are in the global state

    var layoutId,
        regionId;

    // Create dynamic elements for each linked record
    if ( data && layoutModel ) {
        if ( layoutModel.template ) {
            layoutId = layoutModel.template.substr(1);
            if ( layoutId ) {

                // Add linked records header element to the DOM
                if ( layoutModel && layoutModel.template ) {
                    regionId = layoutModel.template.substr(1);
                    if ( regionId ) {
                        createTableTitle( layoutModel, regionId, total );
                        if ( total > 0 ) {
                            createTable( types, layoutModel, data, regionId );
                        }
                    }
                }
            }
        }
    }
}

/**
 * Created linked data table header
 * @param layoutModel {object} - JSON region view model
 * @param regionId {string} - the value of the "id" attribute in the DOM
 * @param total {number} - total number of items in the table
 */
function createTableTitle( layoutModel, regionId, total ) {

    var itemsPerPage = 20,
        headerDiv,
        headerId,
        tableName,
        defaultLayout,
        headerModel,
        LayoutView,
        regionLayout;

    if ( GlobalPageState && GlobalPageState.linksPageNumber && regionId ) {

        // Create the table header DOM element to render the region into
        headerDiv = document.createElement("div");
        headerId = regionId + "-header";
        headerDiv.setAttribute( "id", headerId );
        addDOMElement( headerDiv, regionId );

        tableName = "table-" + regionId;
        // Create header Backbone model
        headerModel = createHeaderModel( tableName, total, itemsPerPage, headerId );

        if ( layoutModel && layoutModel.viewType && headerModel ) {

            // Create default region layout view
            LayoutView = Backbone.Marionette[ layoutModel.viewType ].extend({
                template: '#table-header-template',
                model: BackboneModel
            });

            // Instantiate the region layout with the header model
            regionLayout = new LayoutView({ model: headerModel });

            // Render the data into the region layout
            if ( regionLayout ) {
                defaultLayout = GlobalPageState.layout;
                defaultLayout.addRegion( headerId, '#' + headerId );
                defaultLayout.getRegion( headerId ).show( regionLayout );

                // Listen for pagination events
                switchPageListener( tableName, total, itemsPerPage, layoutModel, regionId );
            }
        }
    }
}

/**
 * Create header Backbone model
 * @param tableName {string} - table name
 * @param total {number} - total number of records
 * @param itemsPerPage {number} - number of records per page
 * @param headerId {string} - header ID
 * @return {object} - Backbone header model
 */
function createHeaderModel( tableName, total, itemsPerPage, headerId ) {
    var headerModel = new BackboneModel,
        page = GlobalPageState.linksPageNumber,
        pagesObject;

    headerModel.set( "total", total );
    headerModel.set( "table_type", "Linked Data" );
    headerModel.set( "table_name", tableName );
    headerModel.set( "page", page );
    headerModel.set( "itemsPerPage", itemsPerPage );
    headerModel.set( "id", headerId );

    // Calculate start and end index of items
    pagesObject = getTablePages( total, itemsPerPage, page );

    if ( pagesObject && pagesObject.start !== undefined && pagesObject.end !== undefined ) {
        headerModel.set( "start", pagesObject.start );
        headerModel.set( "end", pagesObject.end );
    }

    return headerModel;
}

/**
 * Listen for pagination events
 * @param tableName {string} - table name
 * @param total {number} - total number of records
 * @param itemsPerPage {number} - number of records per page
 * @param layoutModel {object} - JSON region view model
 * @param regionId {string} - region ID
 */
function switchPageListener( tableName, total, itemsPerPage, layoutModel, regionId ) {

    var nPages,
        paginationId;

    if ( itemsPerPage !== 0 ) {
        nPages = Math.ceil( total / itemsPerPage );
    }

    if ( GlobalPageState.linksPageNumber && Number.isInteger( GlobalPageState.linksPageNumber ) ) {
        paginationId = tableName + "-page-" + GlobalPageState.linksPageNumber;
        // Right arrow click listen
        $( '#' + paginationId + ' .fa-arrow-right').on('click', function( ) {
            if ( GlobalPageState.linksPageNumber < nPages ) {
                GlobalPageState.linksPageNumber += 1;
                updateLinkedTable( tableName, total, itemsPerPage, layoutModel, regionId );
            }
        });
        // Left arrow click listener
        $( '#' + paginationId + ' .fa-arrow-left').on('click', function( ) {
            if ( GlobalPageState.linksPageNumber > 1 ) {
                GlobalPageState.linksPageNumber -= 1;
                updateLinkedTable( tableName, total, itemsPerPage, layoutModel, regionId );
            }
        });
    }
}

/**
 * Create table header row element
 * @param rowId {string} - table header row ID
 * @param tableHeaderId {string} - table header ID
 */
function makeTableHeaderRow( rowId, tableHeaderId ) {

    var tableHeaderRow = document.createElement( "tr" );
    tableHeaderRow.setAttribute( "id", rowId );
    addDOMElement( tableHeaderRow, tableHeaderId );
}

/**
 * Create table header cell element
 * @param value {string} - display value of the header cell
 * @param rowId {string} - header row ID
 */
function makeTableHeaderCell( value, rowId ) {

    var cell = document.createElement( "th" );
    var cellText = document.createTextNode( value );
    cell.appendChild( cellText );
    addDOMElement( cell, rowId );

}

/**
 * Create table row element
 * @param rowId {string} - table row ID
 * @param tableBodyId {string} - table body ID
 * @param recordType {string} - record type that is shown in this row
 * @param recordId {string} - record ID that is shown in this row
 */
function makeTableRow( rowId, tableBodyId, recordType, recordId ) {

    var row,
        url;

    row = document.createElement( "tr" );
    row.setAttribute( "class", "table-row clickable clickable-link" );
    row.setAttribute( "id", rowId );

    if ( recordType && recordId ) {
        var alias = App.globalState.alias;
        if ( alias ) {
            url = "/view/" + alias + "/" + recordType + "/" + recordId;
        } else {
            url = "/view/" + recordType + "/" + recordId;
        }
        row.setAttribute( "data-href", url );
    }

    addDOMElement( row, tableBodyId );

}


/**
 * Create table cell element
 * @param value {*} - value of the cell
 * @param rowId {string} - table row ID
 */
function makeTableCell( value, rowId ) {

    var cell = document.createElement( "td" );
    if ( value === undefined || value === null ) {
        value = '';
    }
    var cellText = document.createTextNode( value );
    cell.appendChild( cellText );
    addDOMElement( cell, rowId );
}

/**
 * Create table of data
 * @param types {Array} - array of type names
 * @param layoutModel {object} - JSON region view model
 * @param data {Array} - array of linked records
 * @param regionId {string} - region ID
 */
function createTable( types, layoutModel, data, regionId ) {

    var tableWrapper,
        tableWrapperId,
        tableDiv,
        tableId,
        tableHeader,
        tableHeaderId,
        tableHeaderRowId;

    // Create the table header DOM element to render the region into
    if ( data ) {

        // Create table wrapper element

        tableWrapper = document.createElement("div");
        tableWrapperId = regionId + "-table-wrapper";
        tableWrapper.setAttribute( "id", tableWrapperId );
        addDOMElement( tableWrapper, regionId );

        // Create table element
        tableDiv = document.createElement("table");
        tableDiv.setAttribute( "class", "table table-hover" );
        tableId = regionId + "-table";
        tableDiv.setAttribute( "id", tableId);
        addDOMElement( tableDiv, tableWrapperId );


        // Create table header element
        tableHeader = document.createElement("thead");
        tableHeaderId = regionId + "-table-header";
        tableHeader.setAttribute( "id", tableHeaderId );
        addDOMElement( tableHeader, tableId );


        // Display table header data

        // Create table header row
        tableHeaderRowId = regionId + "-table-header-row";
        makeTableHeaderRow( tableHeaderRowId, tableHeaderId );

        // Create table body with data
        makeTableBody( types, tableHeaderRowId, regionId, data );


    }
}

/**
 * Create table body
 * @param types {Array} - linked data type names
 * @param tableHeaderRowId {string} - table header row ID
 * @param regionId {string} - region ID
 * @param data {Array} - array of linked records
 */
function makeTableBody( types, tableHeaderRowId, regionId, data ) {

    var fieldsToDisplay,
        tableBody,
        tableBodyId,
        model,
        field,
        label,
        labelArray,
        prefix,
        fieldLabel,
        fieldObject,
        tableId,
        i;

    // Linked table includes four EDI Common Data fields: ID, Type, Title and Description
    fieldsToDisplay = [ "edi_id", "edi_type", "edi_title", "edi_description" ];

    if ( regionId ) {
        tableId = regionId + "-table";
        if ( types && types.length > 0 ) {
            model = getCommonDataModel( types[0] );
            if ( model && model.properties ) {

                // For each field to display, add its label to the header row
                for ( i = 0; i < fieldsToDisplay.length; i++ ) {
                    field = fieldsToDisplay[ i ];
                    fieldObject = getFieldObjectByName( model, field );

                    if ( fieldObject ) {
                        label = fieldObject.label;
                    }

                    if ( ! label ) {
                        // Set label to modified field name
                        label = field;
                        if ( field ) {
                            labelArray = field.split("_"); // example: "edi_type"
                            if ( labelArray && labelArray.length === 2 && labelArray[ 0 ] && labelArray[ 1 ]) {
                                prefix = labelArray[ 0 ].toUpperCase();
                                fieldLabel = labelArray[ 1 ].capitalizeFirstLetter();
                                label = prefix + " " + fieldLabel;
                            }
                        }
                    }

                    makeTableHeaderCell( label, tableHeaderRowId );
                }

                // Create table body element

                tableBody = document.createElement("tbody");
                tableBodyId = regionId + "-tbody";
                tableBody.setAttribute( "id", tableBodyId );
                addDOMElement( tableBody, tableId );

                // Fill table body with data
                populateTableBody( data, tableBodyId, model );
            }
        }
    }
}

/**
 * Populate table body with data
 * @param data {Array} - array of linked records
 * @param tableBodyId {string} - table body ID
 * @param model {object} - CDM of the linked data
 */
function populateTableBody( data, tableBodyId, model ) {
    var record,
        rowId,
        source,
        field,
        value,
        fieldsToDisplay,
        i,
        j;

    // Linked table includes four EDI Common Data fields: ID, Type, Title and Description
    fieldsToDisplay = [ "edi_id", "edi_type", "edi_title", "edi_description" ];

    if ( data ) {
        // Add row data to the table
        for ( i = 0; i < data.length; i++ ) {
            record = data[ i ];
            if ( record ) {

                // Create new table row
                rowId = "row-" + i;
                makeTableRow( rowId, tableBodyId, record._type, record._id );

                // For each item to display, add it to the row
                for ( j = 0; j < fieldsToDisplay.length; j++ ) {
                    field  = fieldsToDisplay[ j ];

                    if ( field === "edi_type") {
                        value = getModelProperty( model, "label" );
                    } else {
                        source = record._source;
                        if ( source ) {
                            value = source[ fieldsToDisplay[ j ] ];
                        }
                    }
                    makeTableCell( value, rowId );
                }
            }
        }

        // Attach an event listener to the table rows
        tableRowClickListener();
    }
}

/**
 * Listen for click events on a linked records table row
 */
function tableRowClickListener() {
    var route;

    $('.clickable-link').click( function() {
        if ( $(this) ) {
            route = $(this).data("href");
            if ( route ) {
                router.navigate( route, { trigger: true } );
            }
        }
    });
}
/**
 * Update linked table
 * @param tableName {string} - table name
 * @param total {number} - total number of records
 * @param itemsPerPage {number} - number of records per page
 * @param layoutModel {object} - JSON region view model
 * @param regionId {string} - region ID
 */
function updateLinkedTable( tableName, total, itemsPerPage, layoutModel, regionId ) {
    // Update header
    updateLinkedTableHeader( tableName, total, itemsPerPage, layoutModel, regionId );
    // Update body
    updateLinkedTableBody( regionId );
}

/**
 * Update linked table header
 * @param tableName {string} - table name
 * @param total {number} - total number of records
 * @param itemsPerPage {number} - number of records per page
 * @param layoutModel {object} - JSON region view model
 * @param regionId {string} - region ID
 */
function updateLinkedTableHeader( tableName, total, itemsPerPage, layoutModel, regionId ) {

    var LayoutView,
        regionLayout,
        headerModel,
        headerId,
        defaultLayout = GlobalPageState.layout;

    // Create header Backbone model
    if ( regionId ) {
        headerId = regionId + "-header";
        headerModel = createHeaderModel( tableName, total, itemsPerPage, headerId );

        if ( layoutModel && layoutModel.viewType && headerModel ) {

            // Create default region layout view
            LayoutView = Backbone.Marionette[ layoutModel.viewType ].extend({
                template: '#table-header-template',
                model: BackboneModel
            });

            // Instantiate the region layout with the header model
            regionLayout = new LayoutView({ model: headerModel });

            // Render the data into the region layout
            if ( regionLayout && defaultLayout) {
                defaultLayout.getRegion( headerId ).show( regionLayout );

                // Listen for pagination events
                switchPageListener( tableName, total, itemsPerPage, layoutModel, regionId );
            }
        }
    }

}

/**
 * Update linked table body
 * @param regionId {string} - region ID
 */
function updateLinkedTableBody( regionId ) {

    var dataNamesArray = [ "links" ],
        page = GlobalPageState.linksPageNumber,
        apiJSON,
        recordModel,
        urls;

    if ( GlobalPageState ) {
        apiJSON = getJSONModel( "api", GlobalPageState.type, null );

        if ( apiJSON ) {
            // Construct API URL for the data name
            recordModel = new BackboneModel({
                id: GlobalPageState.id,
                pageName: GlobalPageState.currentPage,
                page: page
            });
            urls = constructAPIs( apiJSON, GlobalPageState.currentPage, dataNamesArray, recordModel );

            if ( urls.length > 0 ) {
                // Fetch data from the server
                getLinkedTableData( urls, dataNamesArray, page, regionId );
            }
        }
    }

}

/**
 * Get data for the linked records table
 * @param urls {[string]} -  array of URLs
 * @param dataNamesArray {[string]} - array of data names
 * @param page {number} - page number to get
 * @param regionId {string} - region ID
 */
function getLinkedTableData( urls, dataNamesArray, page, regionId ) {
    var response,
        collection,
        viewJSON,
        k;

    if ( GlobalPageState ) {
        viewJSON = getJSONModel( "view", GlobalPageState.type, null );
        if ( viewJSON ) {
            collection = getBackboneCollection( viewJSON.properties );
            if ( collection ) {
                for ( k = 0; k < urls.length; k++ ) {
                    response  = {};
                    _.each( collection.models, function ( model ) {
                        if ( model.get("page") === GlobalPageState.currentPage ) {
                            makeAjaxRequest( urls[ k ], linkedTableDataFound( regionId ), dataNotFound( urls[ k ]) );
                        }
                    });
                }
            }
        }
    }
}

/**
 * Linked data found in Search API
 * @param regionId {string} - region ID
 * @return {Function}
 */
function linkedTableDataFound( regionId ) {
    return function( data, textStatus, jqXHR ) {
        displayLinkedTableData( data, regionId );
    }
}

/**
 * Display linked records data in existing table
 * @param data {object} - linked records
 * @param regionId {string} - region ID
 */
function displayLinkedTableData( data, regionId ) {
    var response,
        tableBodyId,
        result;

    // Fill table body with data
   if ( regionId && data ) {
       response = getResponseObject( data );
       if ( response ) {
           tableBodyId = regionId + "-tbody";
           $( '#' + tableBodyId ).empty();

           result = getLinkedModelsAndTypesFromData( response );
           if ( result ) {
               if ( result.models && result.models.length > 0 ) {
                   populateTableBody( response, tableBodyId, result.models[ 0 ] );
               }
           }
       }
   }
}